import React, { useEffect, useState } from 'react';
import { useKpros } from '../../context/KprosContext';
import { useTheme } from '../../context/ThemeContext';
import { Lightbulb, Wrench, Sparkles, BookOpen, Filter } from 'lucide-react';

const CATEGORIES = [
  { key: '', label: 'All', icon: BookOpen },
  { key: 'prompt_tips', label: 'Prompt Tips', icon: Sparkles },
  { key: 'workarounds', label: 'Workarounds', icon: Wrench },
  { key: 'discoveries', label: 'Discoveries', icon: Lightbulb },
];

const FEATURE_TAGS = [
  { key: '', label: 'All Features' },
  { key: 'studio', label: 'Studio' },
  { key: 'ait', label: 'AIT' },
  { key: 'data_extraction', label: 'Data Extraction' },
  { key: 'profitability', label: 'Analyzer' },
  { key: 'team_chat', label: 'Team Chat' },
  { key: 'email', label: 'Email' },
];

function getCategoryIcon(category) {
  switch (category) {
    case 'prompt_tips': return <Sparkles size={14} className="text-purple-500" />;
    case 'workarounds': return <Wrench size={14} className="text-orange-500" />;
    case 'discoveries': return <Lightbulb size={14} className="text-orange-500" />;
    default: return <BookOpen size={14} className="text-blue-500" />;
  }
}

export default function KprosTipsFeed() {
  const { tips, tipsLoading, fetchTips } = useKpros();
  const { theme } = useTheme();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [featureFilter, setFeatureFilter] = useState('');

  useEffect(() => {
    const filters = {};
    if (categoryFilter) filters.category = categoryFilter;
    if (featureFilter) filters.feature_tag = featureFilter;
    fetchTips(filters);
  }, [categoryFilter, featureFilter, fetchTips]);

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="flex-shrink-0 px-3 py-2 space-y-2 border-b border-gray-200 dark:border-gray-700">
        {/* Category pills */}
        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setCategoryFilter(cat.key)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                categoryFilter === cat.key
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <cat.icon size={12} />
              {cat.label}
            </button>
          ))}
        </div>
        {/* Feature dropdown */}
        <select
          value={featureFilter}
          onChange={e => setFeatureFilter(e.target.value)}
          className="w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
        >
          {FEATURE_TAGS.map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Tips list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {tipsLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : tips.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            No tips yet. Check back later!
          </div>
        ) : (
          tips.map(tip => (
            <div
              key={tip.id}
              className={`p-3 rounded-lg border ${
                theme === 'dark'
                  ? 'bg-gray-750 border-gray-600'
                  : 'bg-white border-gray-200'
              } shadow-sm`}
            >
              <div className="flex items-center gap-2 mb-1">
                {getCategoryIcon(tip.category)}
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{tip.title}</h4>
              </div>
              {tip.feature_tag && (
                <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 mb-1">
                  {tip.feature_tag}
                </span>
              )}
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                {tip.body}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
